import { defineStore } from 'pinia'

import { getProductPromos } from '@/service/promo'
import { getBookingPaymentAmount } from '@/service/payment'

import type {
  FpaymentCreditCardInfo,
  EatigoOrderCreateOrderReq,
  PaymentPaymentAmountData,
  FpaymentPaymentReqV2,
  PromoProductPromoInfo,
  BranchData,
} from '@/types/api'
import { CurrencyEnum } from '@/types/filters'
import { EdbPaymentMethodProviderType as ApiEdbPaymentMethodProviderType, EdbCurrencyID as ApiEdbCurrencyID } from '@/types/api'
import { PaymentMethodsType } from '@/types/payment'
import type { PaymentMethodData } from '@/types/payment'
import type { SlotInfo } from '@/types/product'
import type { PaymentProviderInfo } from '@/types/booking'

import { PROMO_CODE_UNSELECTED_ID, PROMO_CODE_DEFAULT_ID } from '@/constants/commons'

export const useBookingStore = defineStore('booking', () => {
  const branch = ref<Nullable<BranchData>>(null)
  const bookingDate = ref('')
  const bookingAttendance = ref(0)
  const selectedSlot = ref<Nullable<SlotInfo>>(null)

  const remarks = ref<Record<string, unknown>>({})
  const remarksCreationParams = computed(() =>
    Object.entries(remarks.value).map(([key, value]) => {
      return {
        remark_id: +key,
        answers: Array.isArray(value)
          ? value.map(v => (typeof v === 'object' ? { sub_id: +v.id, content: `${v.value}` } : { sub_id: +v }))
          : [{ content: `${value}` }],
      }
    })
  )
  const hasTax = ref(false)

  const promoList = ref<PromoProductPromoInfo[]>([])
  const selectedPromoInfo = computed(() => promoList.value.find(promo => promo.promo_code_list_id === selectedPromoCodeId.value))
  const selectedPromoCodeId = ref(PROMO_CODE_UNSELECTED_ID)
  const isUsingFunCoin = ref(false)
  const paymentInfo = ref<PaymentPaymentAmountData>({})
  const orderId = ref(0)
  const checkDuplicateOrder = ref(true)
  const isBookingPaymentAmountLoading = ref(false)

  const orderNote = ref('')
  const bookingEmail = ref('')

  const selectedCard = ref<FpaymentCreditCardInfo>({})
  const isCardReady = ref(false)
  /** provider(payment SDK) */
  const paymentProviderInfo = ref<PaymentProviderInfo>({ bincode: '', bankId: '', prime: '' })

  const isPromoSelected = computed(() => selectedPromoCodeId.value !== PROMO_CODE_UNSELECTED_ID)
  const isUsingPromo = computed(() => selectedPromoCodeId.value !== PROMO_CODE_UNSELECTED_ID && selectedPromoCodeId.value !== PROMO_CODE_DEFAULT_ID)
  const isNeedCvv = computed(() => selectedCard.value.currency?.id === ApiEdbCurrencyID.CurrencyIDMYR)
  const isGatewayReady = computed(() => {
    switch (selectedPaymentGateway.value.type) {
      case PaymentMethodsType.SAVED_CREDIT_CARD:
        return !!selectedCard.value?.id && (!isNeedCvv.value || !!paymentProviderInfo.value.cvv)
      case PaymentMethodsType.NEW_CREDIT_CARD:
      case PaymentMethodsType.THREE_INSTALLMENTS:
        return isCardReady.value
      case PaymentMethodsType.LINE_PAY:
        return true
      default:
        return false
    }
  })

  // const hasBookingData = computed(() => bookingStartTime.value && bookingTotalAmount.value)

  const taxData = reactive({
    taxId: '',
    companyName: '',
    receiptCompany: '',
  })

  const selectedPaymentGateway = ref<PaymentMethodData>({})

  const bookingTime = computed(() => selectedSlot.value?.slot)
  const paymentCurrencyCode = computed<string>(() => {
    const defaultCurrencyCode = selectedPaymentGateway.value?.currency?.code ?? paymentInfo.value.currency_code ?? 'TWD'

    if (selectedPaymentGateway.value?.type === PaymentMethodsType.SAVED_CREDIT_CARD) {
      return selectedCard.value.currency?.code ?? defaultCurrencyCode
    }
    return defaultCurrencyCode
  })

  const paymentTxnAmount = computed(() => Number(paymentInfo.value.multi_currency_info?.txn_amount?.[paymentCurrencyCode.value] ?? 0))

  const orderCreateParams = computed<EatigoOrderCreateOrderReq>(() => {
    const params: EatigoOrderCreateOrderReq = {
      booking_time: selectedSlot.value?.slot ?? '',
      discount: selectedSlot.value?.discount ?? 0,
      headcount: bookingAttendance.value,
      member_note: orderNote.value,
      remarks: remarksCreationParams.value,
      check_duplicate: checkDuplicateOrder.value,
      referer: '',
      promo_code_list_id: isUsingPromo.value ? selectedPromoCodeId.value : undefined,
      pid: branch.value?.pid ?? 0,
    }

    return params
  })

  const paymentCreationParams = computed<Partial<FpaymentPaymentReqV2>>(() => {
    const isNewCard =
      selectedPaymentGateway.value.type === PaymentMethodsType.NEW_CREDIT_CARD ||
      selectedPaymentGateway.value.type === PaymentMethodsType.THREE_INSTALLMENTS
    // @TODO: 實作付款時，將 currency 的取得改成從 `useCurrencyStore` 獲得
    const currencyId = CurrencyEnum[paymentCurrencyCode.value as keyof typeof CurrencyEnum]
    const requiredParams = {
      amount: paymentTxnAmount.value,
      prime: paymentProviderInfo.value.prime,
      from: 'Eatigo',
      promo: selectedPromoInfo.value?.id,
      code_id: selectedPromoInfo.value?.id,
      promo_usage: isUsingPromo.value ? 1 : 0,
      bonus: paymentInfo.value.used_fun_coin ? Number(paymentInfo.value.used_fun_coin) : 0,
      currency_id: currencyId,
    }

    switch (selectedPaymentGateway.value.type) {
      case PaymentMethodsType.SAVED_CREDIT_CARD:
      case PaymentMethodsType.NEW_CREDIT_CARD:
      case PaymentMethodsType.THREE_INSTALLMENTS:
        return {
          ...requiredParams,
          bin: paymentProviderInfo.value.bincode,
          is_new_card: isNewCard,
          cvv: paymentProviderInfo.value.cvv,
          cardid: isNewCard ? undefined : selectedCard.value?.id,
          installment: selectedPaymentGateway.value.type === PaymentMethodsType.THREE_INSTALLMENTS ? 3 : undefined,
        }
      case PaymentMethodsType.LINE_PAY:
      default:
        return requiredParams
    }
  })

  async function updatePaymentInfo(params?: Partial<Parameters<typeof getBookingPaymentAmount>[0]>) {
    // @TODO: check required params
    const _params = {
      booking_time: bookingTime.value,
      // amount: bookingStore.bookingTotalOriginAmount,
      // currency_id: productInfo.value?.currency_id ?? 0,
      product_id: branch.value?.pid ?? 0,
      code_id: selectedPromoCodeId.value,
      promo_usage: selectedPromoCodeId.value ? 1 : 0,
      ...params,
    }

    try {
      isBookingPaymentAmountLoading.value = true
      paymentInfo.value = await getBookingPaymentAmount(_params)
    } finally {
      isBookingPaymentAmountLoading.value = false
    }
  }

  async function updatePromoList() {
    if (!bookingTime.value) {
      return
    }

    const pid = branch.value?.pid ?? ''
    const { data } = await getProductPromos(pid, {
      booking_time: bookingTime.value,
    })
    promoList.value = data.list
  }

  async function initialPayment() {
    switch (selectedPaymentGateway.value.type) {
      case PaymentMethodsType.NEW_CREDIT_CARD:
      case PaymentMethodsType.THREE_INSTALLMENTS: {
        if (selectedPaymentGateway.value.provider_type !== ApiEdbPaymentMethodProviderType.PaymentMethodProviderTypeTapPay) {
          break
        }

        const primeResult = await TapPaySDK.getPrime()
        const { bincode, bank_id: bankId, prime } = primeResult.card
        paymentProviderInfo.value = { bincode, bankId, prime }

        break
      }
      case PaymentMethodsType.LINE_PAY: {
        const primeResult = await TapPaySDK.getLinePayPrime()
        const { prime } = primeResult
        paymentProviderInfo.value = { prime }

        break
      }
      default:
        break
    }
  }

  return {
    // product context
    branch,
    bookingAttendance,
    bookingDate,
    selectedSlot,
    remarks,

    bookingTime,
    bookingEmail,
    checkDuplicateOrder,
    selectedCard,
    isCardReady,
    isPromoSelected,
    isNeedCvv,
    isGatewayReady,
    paymentProviderInfo,
    promoList,
    selectedPromoInfo,
    selectedPromoCodeId,
    isUsingFunCoin,
    paymentCurrencyCode,
    paymentTxnAmount,
    orderCreateParams,
    orderNote,
    paymentCreationParams,
    paymentInfo,
    selectedPaymentGateway,
    taxData,
    isBookingPaymentAmountLoading,
    initialPayment,
    updatePaymentInfo,
    updatePromoList,
    orderId,
    hasTax,
  }
})
